import { useState } from 'react';

import { truncate } from 'lodash-es';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface ExpandableAlertProps {
  variant?: 'default' | 'destructive' | 'warning' | 'info';
  icon?: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  expandedContent?: React.ReactNode;
  isExpanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  expandButtonLabel?: string;
  collapseButtonLabel?: string;
  className?: string; // Applied to the Alert container
  descriptionClassName?: string; // Applied to the AlertDescription
  truncateLength?: number; // Maximum string length for truncation
  enableTruncation?: boolean; // Enable/disable description truncation
}

export function ExpandableAlert({
  variant = 'default',
  icon,
  title,
  description,
  expandedContent,
  isExpanded: controlledExpanded,
  defaultExpanded = false,
  onExpandedChange,
  className,
  descriptionClassName,
  truncateLength = 100,
  enableTruncation = true,
}: ExpandableAlertProps) {
  const { t } = useTranslation();
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  const isControlled = controlledExpanded !== undefined;
  const isExpanded = isControlled ? controlledExpanded : internalExpanded;

  const handleToggle = () => {
    const newState = !isExpanded;
    if (!isControlled) {
      setInternalExpanded(newState);
    }
    onExpandedChange?.(newState);
  };

  // Check if description should be truncated
  const shouldTruncate = enableTruncation && typeof description === 'string';
  const isTruncated = shouldTruncate && description.length > truncateLength;

  // Create truncated description if needed
  const displayDescription =
    shouldTruncate && !isExpanded && isTruncated
      ? truncate(description, { length: truncateLength, separator: ' ' })
      : description;

  // Determine if we should show the expand/collapse button
  const shouldShowToggle = !!expandedContent || isTruncated;

  return (
    <Alert variant={variant} className={cn(className)}>
      {icon}
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className={cn('space-y-3', descriptionClassName)}>
        {displayDescription}

        {expandedContent && isExpanded && (
          <div className="duration-200 animate-in slide-in-from-top-2">{expandedContent}</div>
        )}

        {shouldShowToggle && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleToggle}
            className="h-auto p-0 font-normal hover:bg-transparent"
          >
            <span className="flex items-center gap-1 text-sm text-foreground-muted">
              {isExpanded ? (
                <>
                  {t('common.show_less')}
                  <ChevronUpIcon className="h-4 w-4" />
                </>
              ) : (
                <>
                  {t('common.show_more')}
                  <ChevronDownIcon className="h-4 w-4" />
                </>
              )}
            </span>
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}
